/**
 * Pool de efeitos de magia. Cosmético: se a folha não carregar, o combate
 * segue igual. Id só entra se for `[a-z0-9-]+` e estiver no catálogo.
 */
const POOL = 64;
const ID_OK = /^[a-z0-9-]+$/;
const PEITO_Z = 7;

/**
 * A folha de cada magia (techspec-magias-animacoes; docs/prototipos/magias-animacoes-prototipo.html): a arte do
 * cliente da UO nos ids que o RunUO usa (tools/uo-efeitos-magia.py). O Relâmpago já era a arte do UO (gump 0x4E20).
 */
const FOLHA: Readonly<Record<string, string>> = {
  'magic-arrow': 'uo-magic-arrow',
  fireball: 'uo-fireball',
  'energy-bolt': 'uo-energy-bolt',
  harm: 'uo-faisca-vermelha',
  'mind-blast': 'uo-faisca-vermelha',
  clumsy: 'uo-faisca-vermelha',
  feeblemind: 'uo-faisca-vermelha',
  curse: 'uo-faisca-vermelha',
  'mass-curse': 'uo-faisca-vermelha',
  poison: 'uo-veneno',
  weaken: 'uo-enfraquecer',
  paralyze: 'uo-paralisia',
  heal: 'uo-faisca-azul',
  'greater-heal': 'uo-faisca-azul',
  'night-sight': 'uo-faisca-azul',
  'reactive-armor': 'uo-faisca-azul',
  cure: 'uo-faisca-branca',
  'arch-cure': 'uo-faisca-branca',
  bless: 'uo-faisca-branca',
  agility: 'uo-faisca-buff',
  cunning: 'uo-faisca-buff',
  strength: 'uo-faisca-buff',
  protection: 'uo-faisca-buff',
  'arch-protection': 'uo-faisca-buff',
  'magic-reflection': 'uo-faisca-buff',
  explosion: 'uo-explosao',
  flamestrike: 'uo-coluna-fogo',
  dispel: 'uo-fumaca',
  recall: 'uo-fumaca',
  // O bafo dos mobs (backend RoomLoop.MobAtaques): o ServUO solta a bola de fogo 0x36D4; o de gelo é o mesmo voo,
  // aqui com o raio azul do Raio de Energia.
  'bafo-fogo': 'uo-fireball',
  'bafo-gelo': 'uo-energy-bolt'
};

/** Ficam no alvo e tocam uma vez. O fio manda duração 0 nessas magias. */
const ALVO = new Set([
  'lightning', 'uo-faisca-vermelha', 'uo-veneno', 'uo-enfraquecer', 'uo-faisca-azul', 'uo-paralisia',
  'uo-faisca-branca', 'uo-faisca-buff', 'uo-explosao', 'uo-coluna-fogo', 'uo-fumaca',
  // as folhas antigas (desenhadas à parte), que ainda podem ser pedidas por outro caminho
  'flamestrike', 'heal', 'explosion', 'sparkle'
]);

export function folhaDaMagia(spellId: string): string {
  return FOLHA[spellId] ?? spellId;
}

export function ehEfeitoAlvo(id: string): boolean {
  return ALVO.has(id);
}

export type ModoEfeito = 'projetil' | 'alvo';

export interface DefEfeito {
  readonly id: string;
  readonly celula: readonly [number, number];
  readonly quadros: number;
  readonly fps: number;
  readonly ancora: readonly [number, number];
  readonly modo: ModoEfeito;
  readonly rotaciona: boolean;
  readonly aditivo: boolean;
  /** Soma ao atan2. Fireball aponta à esquerda (π); a flecha, à direita (0). */
  readonly rotacao: number;
  readonly impacto: string | null;
  readonly fonte: string;
}

export interface ShotEfeito {
  live: boolean;
  id: string;
  deX: number;
  deY: number;
  deZ: number;
  paraX: number;
  paraY: number;
  paraZ: number;
  inicio: number;
  duracao: number;
  /** ShortId de quem a animação acompanha. 0 fica no tile do impacto. */
  seguir: number;
  /** Projétil: o ShortId do alvo que ele persegue (o destino é o ponto atual do alvo a cada quadro). 0 = fixo. */
  mirar: number;
}

const pool: ShotEfeito[] = Array.from({ length: POOL }, () => vazio());
const defs = new Map<string, DefEfeito>();
const imgs = new Map<string, HTMLImageElement>();
const loading = new Set<string>();
const recusou = new Set<string>();
let catalogo = new Set<string>();
let catalogoPronto: Promise<void> | null = null;

function vazio(): ShotEfeito {
  return {
    live: false, id: '',
    deX: 0, deY: 0, deZ: 0,
    paraX: 0, paraY: 0, paraZ: 0,
    inicio: 0, duracao: 1, seguir: 0, mirar: 0
  };
}

export function peitoZ(): number {
  return PEITO_Z;
}

export function duracaoVoo(distCasas: number): number {
  return Math.min(600, Math.max(120, distCasas * 60));
}

function chebyshev(ax: number, ay: number, bx: number, by: number): number {
  return Math.max(Math.abs(bx - ax), Math.abs(by - ay));
}

function pastaDoEfeito(id: string): string {
  return id.startsWith('guarda-') ? '/effects/guarda' : '/effects/skills';
}

function prefetchCatalogo(): Promise<void> {
  if (catalogoPronto) return catalogoPronto;
  catalogoPronto = Promise.all([
    fetch('/effects/skills/catalog.json', { cache: 'no-store' }).then(res => (res.ok ? res.json() : { ids: [] })),
    fetch('/effects/guarda/catalog.json', { cache: 'no-store' }).then(res => (res.ok ? res.json() : { ids: [] })).catch(() => ({ ids: [] }))
  ])
    .then(lotes => {
      const next = new Set<string>();
      for (const data of lotes as Array<{ ids?: unknown }>) {
        for (const id of Array.isArray(data.ids) ? data.ids : []) {
          if (typeof id === 'string' && ID_OK.test(id)) next.add(id);
        }
      }
      catalogo = next;
    })
    .catch(() => {
      catalogo = new Set();
    });
  return catalogoPronto;
}

function slotLivre(): number {
  let oldest = 0;
  let oldestT = Infinity;
  for (let i = 0; i < POOL; i++) {
    const s = pool[i];
    if (!s.live) return i;
    if (s.inicio < oldestT) {
      oldestT = s.inicio;
      oldest = i;
    }
  }
  return oldest;
}

function parseDef(id: string, raw: unknown): DefEfeito | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (typeof o.fonte !== 'string' || !o.fonte) return null;
  const cel = o.celula;
  const anc = o.ancora;
  if (!Array.isArray(cel) || cel.length < 2) return null;
  if (!Array.isArray(anc) || anc.length < 2) return null;
  const modo = o.modo === 'alvo' ? 'alvo' : o.modo === 'projetil' ? 'projetil' : null;
  if (!modo) return null;
  const quadros = Number(o.quadros);
  const fps = Number(o.fps);
  if (!Number.isFinite(quadros) || quadros < 1 || !Number.isFinite(fps) || fps <= 0) return null;
  return {
    id,
    celula: [Number(cel[0]), Number(cel[1])],
    quadros: quadros | 0,
    fps,
    ancora: [Number(anc[0]), Number(anc[1])],
    modo,
    rotaciona: o.rotaciona !== false,
    aditivo: o.aditivo !== false,
    rotacao: typeof o.rotacao === 'number' && Number.isFinite(o.rotacao) ? o.rotacao : Math.PI,
    impacto: typeof o.impacto === 'string' ? o.impacto : null,
    fonte: o.fonte
  };
}

function ensureSheet(id: string): void {
  if (defs.has(id) || loading.has(id) || recusou.has(id)) return;
  if (!ID_OK.test(id)) return;
  if (catalogo.size > 0 && !catalogo.has(id)) return;
  loading.add(id);
  const pasta = pastaDoEfeito(id);
  void fetch(`${pasta}/${id}.json`, { cache: 'no-store' })
    .then(res => {
      if (!res.ok) throw new Error(String(res.status));
      return res.json();
    })
    .then(raw => {
      const def = parseDef(id, raw);
      if (!def) throw new Error('json');
      defs.set(id, def);
      const marca = raw && typeof raw === 'object' && typeof (raw as { hash?: unknown }).hash === 'string'
        ? (raw as { hash: string }).hash.slice(0, 12)
        : '';
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          imgs.set(id, img);
          resolve();
        };
        img.onerror = () => reject(new Error('png'));
        img.src = marca ? `${pasta}/${id}.png?v=${marca}` : `${pasta}/${id}.png`;
      });
    })
    .catch(() => {
      recusou.add(id);
    })
    .finally(() => {
      loading.delete(id);
    });
}

export function prefetchEfeito(id: string): void {
  if (!ID_OK.test(id)) return;
  void prefetchCatalogo().then(() => ensureSheet(id));
}

export function defEfeito(id: string): DefEfeito | undefined {
  return defs.get(id);
}

export function imgEfeito(id: string): HTMLImageElement | undefined {
  return imgs.get(id);
}

export function spawnProjetil(
  id: string,
  de: { x: number; y: number; z: number },
  para: { x: number; y: number; z: number },
  _inicio: number,
  duracaoMs?: number,
  seguir = 0,
  mirar = 0
): void {
  if (!ID_OK.test(id)) return;
  if (catalogo.size > 0 && !catalogo.has(id)) return;
  ensureSheet(id);
  const alvo = ALVO.has(id);
  const voo = alvo
    ? 8_000
    : duracaoMs && duracaoMs > 0
      ? duracaoMs
      : duracaoVoo(chebyshev(de.x, de.y, para.x, para.y));
  const i = slotLivre();
  const s = pool[i];
  s.live = true;
  s.id = id;
  s.deX = de.x;
  s.deY = de.y;
  s.deZ = de.z;
  s.paraX = para.x;
  s.paraY = para.y;
  s.paraZ = para.z;
  // O servidor já lançou nos últimos tiques do hit. Começar agora,
  // sem rebobinar o relógio, para a bola chegar junto do `v`.
  s.inicio = performance.now();
  s.duracao = Math.max(1, voo);
  s.seguir = seguir;
  s.mirar = alvo ? 0 : mirar;
  const t0 = s.inicio;
  void prefetchCatalogo().then(() => {
    if (!s.live || s.inicio !== t0) return;
    if (catalogo.size > 0 && !catalogo.has(id)) s.live = false;
  });
}

export function forEachProjetil(
  agora: number,
  fn: (s: ShotEfeito, p: number, tx: number, ty: number, tz: number) => void,
  /** Onde está agora quem o projétil persegue (techspec-magias-animacoes). Nulo: o alvo sumiu, voa até o último ponto. */
  posDoAlvo?: (id: number) => { x: number; y: number } | null
): void {
  for (let i = 0; i < POOL; i++) {
    const s = pool[i];
    if (!s.live) continue;
    const def = defs.get(s.id);
    const alvo = def?.modo === 'alvo';
    const dur = alvo ? (def.quadros / def.fps) * 1000 : s.duracao;
    const p = (agora - s.inicio) / dur;
    if (p >= 1) {
      s.live = false;
      continue;
    }
    const t = p < 0 ? 0 : p;
    if (alvo) {
      fn(s, t, s.paraX, s.paraY, s.paraZ);
      continue;
    }
    if (s.mirar && posDoAlvo) {
      const alvoAgora = posDoAlvo(s.mirar);
      if (alvoAgora) {
        s.paraX = alvoAgora.x;
        s.paraY = alvoAgora.y;
      }
    }
    const tx = s.deX + (s.paraX - s.deX) * t;
    const ty = s.deY + (s.paraY - s.deY) * t;
    const tz = s.deZ + (s.paraZ - s.deZ) * t + PEITO_Z;
    fn(s, t, tx, ty, tz);
  }
}

void prefetchCatalogo().then(() => {
  for (const id of ALVO) ensureSheet(id);
});
